export function sliderMidValue({ min, max, midValue, step }) {
  const raw =
    Number.isFinite(Number(midValue)) && midValue > min && midValue < max
      ? Number(midValue)
      : min + (max - min) * 0.5;
  const stepped = Math.round(raw / step) * step;
  const decimals = (String(step).split(".")[1] || "").length;
  return parseFloat(stepped.toFixed(decimals));
}

export { sliderFillPct } from "./slider-math.js";

export function formatAdjustableTileValue({ min, max, value, format }) {
  if (format) return format(value);
  if (min === 0 && max === 1) {
    return value.toFixed(2);
  }
  return String(value);
}

import { REVERB_AMOUNT_STEP } from "./reverb-steps.js";

export function sliderValueFromClientX(
  clientX,
  el,
  { min, max, midValue, step },
) {
  const rect = el.getBoundingClientRect();
  if (rect.width <= 0) return min;
  const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
  const hasCustomMid =
    Number.isFinite(Number(midValue)) && midValue > min && midValue < max;
  const raw = hasCustomMid
    ? ratio <= 0.5
      ? min + (ratio / 0.5) * (midValue - min)
      : midValue + ((ratio - 0.5) / 0.5) * (max - midValue)
    : min + ratio * (max - min);
  const stepped = Math.round(raw / step) * step;
  const decimals = (String(step).split(".")[1] || "").length;
  return parseFloat(stepped.toFixed(decimals));
}

const ADJUSTABLE_SLIDER_SPECS = [
  {
    key: "highPass",
    activeProp: "highPassMode",
    min: 80,
    max: 3500,
    step: 50,
    valueProp: "highPassCutoff",
    fallback: 500,
  },
  {
    key: "lowPass",
    activeProp: "lowPassMode",
    min: 200,
    max: 3500,
    midValue: 1000,
    step: 50,
    valueProp: "lowPassCutoff",
    fallback: 1000,
    defaultValue: 1000,
  },
  {
    key: "bitcrusher",
    activeProp: "bitcrusherMode",
    min: 0,
    max: 1,
    step: 0.01,
    valueProp: "bitcrusherAmount",
    fallback: 0.45,
  },
  {
    key: "bassBoost",
    activeProp: "bassBoostMode",
    min: 0,
    max: 1,
    step: 0.01,
    valueProp: "bassBoostAmount",
    fallback: 0.5,
  },
  {
    key: "phaser",
    activeProp: "phaserMode",
    min: 0,
    max: 1,
    step: 0.01,
    valueProp: "phaserAmount",
    fallback: 0.5,
  },
  {
    key: "lofiTape",
    activeProp: "lofiTapeMode",
    min: 0,
    max: 1,
    step: 0.01,
    valueProp: "lofiHissVolume",
    fallback: 1,
  },
  {
    key: "rainy",
    activeProp: "rainyMode",
    min: 0,
    max: 1,
    step: 0.01,
    valueProp: "rainVolume",
    fallback: 0.6,
  },
  {
    key: "coffeeShop",
    activeProp: "coffeeShopMode",
    min: 0,
    max: 1,
    step: 0.01,
    valueProp: "coffeeShopVolume",
    fallback: 0.6,
  },
  {
    key: "seashore",
    activeProp: "seashoreMode",
    min: 0,
    max: 1,
    step: 0.01,
    valueProp: "seashoreVolume",
    fallback: 0.6,
  },
  {
    key: "berghain",
    activeProp: "berghainMode",
    min: 0,
    max: 1,
    step: 0.01,
    valueProp: "berghainVolume",
    fallback: 0.6,
  },
  {
    key: "hotelLounge",
    activeProp: "hotelLoungeMode",
    min: 0,
    max: 1,
    step: 0.01,
    valueProp: "hotelLoungeVolume",
    fallback: 0.6,
  },
  {
    key: "cocktailBar",
    activeProp: "cocktailBarMode",
    min: 0,
    max: 1,
    step: 0.01,
    valueProp: "cocktailBarVolume",
    fallback: 0.6,
  },
  {
    key: "vinylCrackle",
    activeProp: "vinylCrackleMode",
    min: 0,
    max: 1,
    step: 0.01,
    valueProp: "vinylCrackleVolume",
    fallback: 0.55,
  },
  {
    key: "reverb",
    isActive: (props) => (props.reverbAmount ?? 0) > 0,
    min: 0,
    max: 1,
    step: REVERB_AMOUNT_STEP,
    valueProp: "reverbAmount",
    fallback: 0,
  },
  {
    key: "echo",
    activeProp: "echoMode",
    min: 0,
    max: 1,
    step: 0.01,
    valueProp: "echoAmount",
    fallback: 0.5,
  },
];

function isAdjustableSliderActive(props, spec) {
  if (spec.isActive) return spec.isActive(props);
  return !!props[spec.activeProp];
}

export function seedSliderTouchedKeys(props) {
  const touched = {};

  ADJUSTABLE_SLIDER_SPECS.forEach(
    ({ key, min, max, step, valueProp, fallback, defaultValue, ...spec }) => {
      if (!isAdjustableSliderActive(props, spec)) return;
      const value =
        typeof props[valueProp] === "number" ? props[valueProp] : fallback;
      const initialValue = defaultValue ?? sliderMidValue({ min, max, step });
      if (Math.abs(value - initialValue) > step / 2) {
        touched[key] = true;
      }
    },
  );

  return touched;
}

export const SHUFFLE_SYNC_PROPS = [
  "slowControl",
  "reverbAmount",
  "videoFx",
  "lowPassMode",
  "highPassMode",
  "bitcrusherMode",
  "bassBoostMode",
  "phaserMode",
  "echoMode",
  "lofiTapeMode",
  "rainyMode",
  "coffeeShopMode",
  "seashoreMode",
  "berghainMode",
  "hotelLoungeMode",
  "cocktailBarMode",
  "vinylCrackleMode",
  "preservePitchMode",
  "lowPassCutoff",
  "highPassCutoff",
  "bitcrusherAmount",
  "bassBoostAmount",
  "phaserAmount",
  "echoAmount",
  "lofiHissVolume",
  "rainVolume",
  "coffeeShopVolume",
  "seashoreVolume",
  "berghainVolume",
  "hotelLoungeVolume",
  "cocktailBarVolume",
  "vinylCrackleVolume",
];

export function hasShuffleSyncPropsChanged(prevProps, nextProps) {
  return SHUFFLE_SYNC_PROPS.some((prop) => prevProps[prop] !== nextProps[prop]);
}
